/**
 * @module main
 *
 * @description The main entry point for the app.
 */
import { createApp, defineAsyncComponent, h } from 'vue';
import { createPinia } from 'pinia';
import { createI18n } from 'vue-i18n';
import { appRouter, locationRouter } from './router';
import VueKonva from 'vue-konva';
import defaultI18nConfig, { createI18nConfig } from './app/locales';
import { location, ui, context, window as SwWindow } from '@shopware-ag/meteor-admin-sdk';
import { DeviceHelperPlugin, TooltipDirective } from '@shopware-ag/meteor-component-library';

/**
 * Send ready state to parent window to indicate the app has finished loading
 */
function sendReadyState() {
    window.parent.postMessage('sw-app-loaded', '*');
}

const locations: { [k:string]: any } = {
    'sw-service-payment-modal': defineAsyncComponent(() => import('./app/components/ServicePaymentModal.vue')),
    'sw-image-editor': defineAsyncComponent(() => import('./App.vue')),
    'sw-permission-missing-modal': defineAsyncComponent(() => import('./app/components/PermissionMissingModal.vue')),
};

function renderLocation() {
    const locationId = location.get();
    const defaultComponent = locations[locationId];

    // Return null for unknown locations to prevent Vue errors
    if (!defaultComponent) {
        return null;
    }

    return h(defaultComponent);
}

// Initialize the app
export async function initializeApp() {
    if (location.is(location.MAIN_HIDDEN)) {
        try {
            ui.menu.addMenuItem({
                label: 'sw-image-editor.menuTitle',
                locationId: 'sw-image-editor',
                displaySearchBar: true,
                displaySmartBar: false,
                parent: 'sw-content',
            });

            ui.actionButton.add({
                name: 'open-image-editor',
                entity: 'media',
                view: 'item',
                label: 'sw-image-editor.buttonOpenImageEditor',
                fileTypes: ['png', 'jpg', 'jpeg'],
                meteorIcon: 'regular-tools-alt',
                callback: async (entity) => {
                    const { modules } = await context.getModuleInformation();
                    const imageEditorModule = modules.find(module => module.locationId === 'sw-image-editor');

                    if (!imageEditorModule) {
                        console.error('Image editor module not found');
                        return;
                    }

                    localStorage.setItem('editMedia', JSON.stringify(entity));
                    SwWindow.routerPush({
                        name: 'sw.extension.sdk.index',
                        params: {
                            id: imageEditorModule.id,
                        },
                    });
                },
            });
        } catch {
            // Do nothing if there is an error checking ACL or license
        }

        // For MAIN_HIDDEN, no UI needs to be mounted
        return;
    }

    // Wait for i18n configuration to be loaded with proper locale
    let i18nConfig;
    try {
        i18nConfig = await createI18nConfig();
    } catch {
        // Fallback to default config (static import only)
        i18nConfig = defaultI18nConfig;
    }

    const app = createApp({
        render: renderLocation,
    });

    // Install i18n for all locations
    app.use(createI18n(i18nConfig));
    app.directive('tooltip', TooltipDirective);

    if (location.get() === 'sw-image-editor') {
        const pinia = createPinia();

        app.use(pinia);
        app.use(VueKonva);
        app.use(DeviceHelperPlugin);
        app.use(appRouter);
    } else {
        app.use(locationRouter);
    }


    app.mount('#app');

    // Start auto resizer for proper iframe sizing
    location.startAutoResizer();

    // Send ready state to parent window to indicate the app has finished loading
    sendReadyState();
}

// Initialize the app
initializeApp();
